import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Alert, Button, Card, Page, PageHeader, Select, Switch } from '@evogard/ui';
import type { RetentionOperator } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import {
  RETENTION_KEY,
  useInvalidateRetention,
  useRetentionTenants,
} from './useRetention.js';

/**
 * A escala do Kanban Cancelamento, por base — a aba "Usuários" do legado.
 *
 * A ordem da lista é a ordem do rodízio: o sistema dá o próximo pedido a quem
 * vem depois de quem recebeu por último. "No rodízio" desligado é o
 * `round_robin_ativo` do legado — a pessoa continua com as colunas e os
 * cartões, mas não recebe pedido novo.
 */
export function OperatorsScreen() {
  const tenants = useRetentionTenants();
  const { data: context } = useSessionContext();
  const [tenant, setTenant] = useState('');
  useEffect(() => {
    if (!tenant && context) setTenant(context.currentTenant.slug);
  }, [tenant, context]);
  const invalidate = useInvalidateRetention();

  const query = useQuery({
    queryKey: [...RETENTION_KEY, 'escala', tenant],
    enabled: tenant !== '',
    queryFn: async () =>
      ok(
        await api.retention.operators({ params: { tenant } }),
        200,
        'Não foi possível carregar a escala.',
      ),
  });
  const [list, setList] = useState<RetentionOperator[]>([]);
  useEffect(() => {
    if (query.data) setList(query.data.operators.filter((o) => o.listed));
  }, [query.data]);
  const [adding, setAdding] = useState('');

  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.retention.saveOperators({
          params: { tenant },
          body: {
            operators: list.map((o) => ({
              userId: o.userId,
              rotationEnabled: o.rotationEnabled,
            })),
          },
        }),
        200,
        'Não foi possível salvar a escala.',
      ),
    onSuccess: invalidate,
  });

  const move = (i: number, by: -1 | 1) =>
    setList((l) => {
      const next = [...l];
      const [item] = next.splice(i, 1);
      next.splice(i + by, 0, item!);
      return next;
    });
  const outside = (query.data?.candidates ?? []).filter(
    (c) => !list.some((o) => o.userId === c.id),
  );

  return (
    <Page>
      <PageHeader title="Escala e rodízio">
        Quem atende o Kanban Cancelamento em cada base, na ordem do rodízio.
      </PageHeader>
      <Select label="Base" value={tenant} onChange={(e) => setTenant(e.target.value)}>
        {tenants.map((t) => (
          <option key={t.slug} value={t.slug}>
            {t.name}
          </option>
        ))}
      </Select>
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      <Card>
        {list.length === 0 ? (
          <p className="text-sm text-texto-suave">
            Ninguém na escala desta base. Os pedidos ficam no Início até alguém entrar.
          </p>
        ) : (
          <ol className="grid gap-3">
            {list.map((o, i) => (
              <li
                key={o.userId}
                className="flex flex-wrap items-center justify-between gap-3"
              >
                <span>
                  {i + 1}. {o.name}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  <Switch
                    checked={o.rotationEnabled}
                    onCheckedChange={(v) =>
                      setList((l) =>
                        l.map((x) =>
                          x.userId === o.userId ? { ...x, rotationEnabled: v } : x,
                        ),
                      )
                    }
                    onLabel="No rodízio"
                    offLabel="Fora do rodízio"
                    label={`${o.name} no rodízio`}
                  />
                  <Button
                    size="sm"
                    variant="discreto"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    Subir
                  </Button>
                  <Button
                    size="sm"
                    variant="discreto"
                    disabled={i === list.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    Descer
                  </Button>
                  <Button
                    size="sm"
                    variant="discreto"
                    onClick={() => setList((l) => l.filter((x) => x.userId !== o.userId))}
                  >
                    Tirar
                  </Button>
                </span>
              </li>
            ))}
          </ol>
        )}
      </Card>
      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Incluir na escala"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
        >
          <option value="">Escolha quem pode tratar cancelamento</option>
          {outside.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Button
          variant="contorno"
          disabled={!adding}
          onClick={() => {
            const person = outside.find((c) => c.id === adding);
            if (person) {
              setList((l) => [
                ...l,
                {
                  userId: person.id,
                  name: person.name,
                  rotationEnabled: true,
                  listed: true,
                },
              ]);
            }
            setAdding('');
          }}
        >
          Incluir
        </Button>
      </div>
      {save.isError && <Alert tone="erro">{save.error.message}</Alert>}
      {save.isSuccess && <Alert tone="sucesso">Escala salva.</Alert>}
      <div>
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Salvar a escala
        </Button>
      </div>
    </Page>
  );
}
